import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Heart, Activity, CheckCircle, HelpCircle, ArrowRight, Shield, ShoppingBag, Zap } from 'react-feather';

const NutritionalSupplements = () => {
  const navigate = useNavigate();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1693996045346-d0a9b9470909?q=80&w=2070&auto=format&fit=crop"
            alt="Nutritional Supplements"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-green-600/40" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-white"
          >
            <div className="inline-flex items-center space-x-2 bg-green-500/20 backdrop-blur-md border border-green-400/30 rounded-full px-4 py-2 mb-6">
              <Zap className="h-4 w-4 text-green-200" />
              <span className="text-sm font-semibold tracking-wide uppercase">Natural Vitality</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Nutritional <br />
              <span className="text-green-200">Supplements</span>
            </h1>
            <p className="text-xl text-green-50 mb-8 leading-relaxed">
              Premium vitamins, minerals, and herbal remedies tailored to your individual needs for optimal health naturally.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 -mt-20 relative z-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Info & Quality */}
            <div className="lg:col-span-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-12"
              >
                {/* Intro Card */}
                <motion.div variants={itemVariants} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                    <Heart className="mr-4 text-green-500" />
                    Why Choose Our Supplements?
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    Discover a variety of nutritional supplements designed to support your health and wellbeing. 
                    We offer high-quality products sourced from reputable manufacturers, tested for purity and potency.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      "Reputable manufacturer sourcing",
                      "Immune, energy & bone support",
                      "Purity & safety tested products",
                      "Expert guidance for your lifestyle"
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-start p-4 bg-green-50 rounded-2xl border border-green-100">
                        <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                        <span className="font-semibold text-gray-800">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* How to Get Started */}
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-600 to-green-700 p-8 md:p-12 rounded-3xl shadow-xl text-white relative overflow-hidden">
                  <div className="absolute right-0 bottom-0 opacity-10">
                    <ShoppingBag size={200} />
                  </div>
                  <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-6">How to Get Started</h2>
                    <p className="text-xl text-green-50 leading-relaxed mb-8">
                      Visit our store or contact us to speak with a health professional who can guide you through selecting the best supplements. 
                      We offer personalized consultations to support your journey.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-5 py-2">
                        <Shield className="h-5 w-5 mr-2" />
                        <span>Certified Quality</span>
                      </div>
                      <div className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-5 py-2">
                        <Activity className="h-5 w-5 mr-2" />
                        <span>Personalized Advice</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div variants={itemVariants} className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                    <HelpCircle className="mr-4 text-green-500" />
                    Common Questions
                  </h2>
                  <div className="grid gap-6">
                    {[
                      {
                        q: "Are your supplements safe to use?",
                        a: "Yes, all our supplements are tested for safety, purity, and potency. We only source products from reputable manufacturers who meet the highest industry standards."
                      },
                      {
                        q: "How do I know which supplements are right for me?",
                        a: "Our team of health professionals can help you determine which supplements are best suited to your individual needs based on your health goals and dietary restrictions."
                      }
                    ].map((faq, index) => (
                      <div key={index} className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">Q: {faq.q}</h3>
                        <p className="text-gray-600 leading-relaxed">A: {faq.a}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Right Column: CTA / Sticky Info */}
            <div className="lg:col-span-4">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="sticky top-24"
              >
                <div className="bg-gray-900 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/20 blur-3xl -mr-16 -mt-16 rounded-full" />
                  <h3 className="text-2xl font-bold mb-4 relative z-10">Boost your wellness</h3>
                  <p className="text-gray-400 mb-8 relative z-10">
                    Explore our comprehensive range of supplements and start your journey to a more vibrant life today.
                  </p>
                  <button 
                    onClick={() => navigate('/shop?category=supplement')}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-2xl transition-all duration-300 flex items-center justify-center group"
                  >
                    Shop Supplements
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                  
                  <div className="mt-8 pt-8 border-t border-gray-800 space-y-4">
                    <div className="flex items-center text-gray-300">
                      <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                        <ShoppingBag className="h-5 w-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Premium Selection</p>
                        <p className="font-semibold">Wide Range of Vitamins</p>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                        <Shield className="h-5 w-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Trust</p>
                        <p className="font-semibold">Quality Guaranteed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default NutritionalSupplements;
